import {
  DocumentArrowDownIcon,
  EnvelopeIcon,
  EyeIcon,
  PaperAirplaneIcon,
  PencilSquareIcon,
} from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { EmailLinksEditor, IEmailButton } from 'components/EmailLinksEditor'
import { LayoutLoading } from 'components/LayoutLoading'
import { SendEmailDialog } from 'components/SendEmailDialog'
import type { InputType } from 'config/input.type.constants'
import { useSharedLoan } from 'context/SharedLoanContext'
import { StripeIntentDialog } from 'pages/Loan'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import {
  createLoanServiceExtension,
  deleteLoanServiceExtension,
  downloadExtensionDocument,
  emailExtension,
  getExtensionDocumentDefaults,
  getExtensionDocumentTemplates,
  getExtensionSignatureRecipients,
  getLoanServiceExtension,
  getStripeIntent,
  postServicingNotes,
  sendEmailForLoanServiceExtension,
  sendExtensionDocument,
  sendExtensionForSignature,
  updateLoanServiceExtension,
  updatePaymentStatus,
} from 'services'
import Api from 'services/api'
import { FormTable, FormTableHeader } from 'stories/components'
import {
  borrowerRecipients,
  confirm,
  emailDirDefaults,
  emailObj2Txt,
  formatTime,
  InputConvert,
  InputValidate,
} from 'utils'
import { RenderInput } from 'utils/RenderInput'
import { setLoanNumber } from 'utils/setLoanNumber'

import {
  extensionInputLogic,
  IMPORTED_EXTENSION_STATUS,
  loanExtensionInputs,
  NO_FEE_EXTENSION_STATUS,
  NOT_SENT_EXTENSION_STATUS,
} from './constant'

const header: FormTableHeader[] = [
  { key: 'id', title: 'Extension ID' },
  {
    key: 'paymentAmount',
    title: 'Fee',
  },
  {
    key: 'feasibilityType',
    title: 'Type',
  },
  {
    key: 'signer',
    title: (
      <span className="whitespace-nowrap">
        Signer Name /<br />
        Email
      </span>
    ),
  },
  // Named for what it actually reports. Every value here - Sent, Paid, Pending Payment, Not Sent -
  // is about the extension FEE request; the agreement's own state is the Signature column. Labelled
  // "Status" it read as "the agreement has been sent", which it never meant.
  { key: 'divStatus', title: 'Fee Request' },
  { key: 'signatureStatus', title: 'Signature' },
  {
    key: 'createdBy',
    title: (
      <span className="whitespace-nowrap">
        Requested Date /<br />
        By
      </span>
    ),
  },
]

/** This grid renders its cells as raw HTML, so anything taken from loan data is escaped first. */
const escapeHtml = (value: any) =>
  `${value ?? ''}`.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')

const getNotesInputs = (): Record<string, InputType> => ({
  notes: {
    inputType: 'textarea',
    title: 'Global Notes',
    rows: 4,
    value: '',
    span: 4,
    visible: true,
    required: false,
  },
})

export default function LoanExtension() {
  const shared = useSharedLoan()
  const isShared = !!shared?.readOnly
  const [loading, setLoading] = useState(true)
  const [data, setData] = useState<Array<any>>([])
  const [inputs, setInputs] = useState<Record<string, InputType>>(loanExtensionInputs())
  const [noteInputs, setNoteInputs] = useState<Record<string, InputType>>(getNotesInputs())
  // Blur can run before the keystroke's state update lands, so the save reads this, not state.
  const noteValueRef = useRef('')
  const [links, setLinks] = useState<IEmailButton[]>([])
  const linksRef = useRef<IEmailButton[]>([])
  const [stripeIntent, setStripeIntent] = useState<Record<string, any> | null>(null)
  // Whether this tenant offers the send that emails the document without asking for a signature.
  // Defaults to showing it, so a servicer never loses the button because the config call failed.
  const [allowEmailWithoutSignature, setAllowEmailWithoutSignature] = useState(true)
  // Whether a new extension must carry a fee (Extension Defaults). A ref, because inputLogic reads it.
  const requireFeeRef = useRef(true)
  // The extension the email dialog is open for, and whether to attach its rendered agreement.
  const [emailRow, setEmailRow] = useState<Record<string, any> | null>(null)
  const [attachAgreement, setAttachAgreement] = useState(false)
  const borrower = useSelector((state: any) => state.borrower?.borrower)
  const borrowerDefaults = useMemo(() => emailDirDefaults(borrowerRecipients(borrower)), [borrower])
  // Prefills and template options are kept in refs as well, because inputLogic runs inside
  // FormTable and would otherwise close over a stale copy.
  const defaultsRef = useRef<Record<string, any>>({})
  const templateOptionsRef = useRef<Record<string, string>>({})

  const init = async () => {
    setLoading(true)

    try {
      const [res, defaultsRes, templatesRes] = await Promise.all([
        getLoanServiceExtension(),
        // Docgen is a separate service: when it is down the document picker just stays
        // empty, it must not take the extensions list down with it.
        getExtensionDocumentDefaults().catch(() => null),
        getExtensionDocumentTemplates().catch(() => null),
      ])
      if (res.success) {
        setData(res.data)
        onChangeNote('notes', res.globalNotes)
        linksRef.current = res.extensionLinks || []
        setLinks(res.extensionLinks || [])
      }

      defaultsRef.current = defaultsRes?.defaults || {}
      setAllowEmailWithoutSignature(defaultsRes?.options?.allowEmailWithoutSignature !== false)
      requireFeeRef.current = defaultsRes?.options?.requireFee !== false
      const templateOptions: Record<string, string> = {}
      ;(templatesRes?.documents || []).forEach((doc: any) => {
        templateOptions[doc.name] = doc.name
      })
      templateOptionsRef.current = templateOptions

      let temp = cloneDeep(inputs)
      temp = inputLogic(temp)
      setInputs(temp)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (isShared) {
      const res = shared.pageData as any
      if (res?.success) {
        setData(res.data || [])
        onChangeNote('notes', res.globalNotes || '')
        setLinks(res.extensionLinks || [])
      }
      let temp = cloneDeep(inputs)
      temp = inputLogic(temp)
      setInputs(temp)
      setLoading(false)
      return
    }
    setLoanNumber()
    init()
  }, [isShared, shared?.pageKey, shared?.pageData])

  const onChoose = (item: any, index: number) => {
    if (item.intentId) onOpenHistory(item.intentId, index + 1)
    // An imported extension has no payment to open and no signature request to re-send; offering
    // to email the borrower about one the prior servicer granted would be wrong.
    else if (item.status !== IMPORTED_EXTENSION_STATUS && item.status !== NO_FEE_EXTENSION_STATUS) onSendEmail(item)
  }

  const getStatusBadgeClass = (status: string) => {
    if (status === 'Paid') return 'bg-green-100 text-green-700'
    if (status === 'Pending Payment' || status === 'Requires Action') return 'bg-amber-100 text-amber-700'
    // An extension carried over from a prior servicer was neither paid nor requested here, so it
    // gets a neutral badge. Without one it falls to the catch-all below and reads as a failure.
    if (status === IMPORTED_EXTENSION_STATUS || status === NO_FEE_EXTENSION_STATUS) return 'bg-slate-100 text-slate-600'
    // Nothing went out. Amber rather than red: it wants attention, but it is not a failed payment.
    if (status === NOT_SENT_EXTENSION_STATUS) return 'bg-amber-100 text-amber-700'
    if (status === 'Failed' || status === 'Cancelled') return 'bg-red-100 text-red-700'
    return 'bg-red-100 text-red-700'
  }

  const onOpenHistory = async (intentId: number, index: number) => {
    if (!intentId) return
    setLoading(true)
    try {
      let stripeIntent = await getStripeIntent(intentId)
      if (stripeIntent?.intentId) {
        await updatePaymentStatus(stripeIntent.intentId)
        stripeIntent = await getStripeIntent(intentId)
        await init()
      }
      const { status } = stripeIntent

      if (status == 'requires_action') {
        const url = stripeIntent.response.next_action.verify_with_microdeposits.hosted_verification_url
        var windowReference: any = window.open()
        windowReference.location = url
      } else {
        setStripeIntent({
          index,
          ...stripeIntent,
        })
      }
    } finally {
      setLoading(false)
    }
  }

  const { email } = useSelector((state: any) => state.auth.profile)

  const onSendEmail = async (item: any) => {
    const content = (
      <div className="text-gray-400 mb-4 text-[18px]">
        Do you want to send email again?
        <br />
        <span className="text-gray-600">Extension ID: {item.id}</span>
      </div>
    )
    const result = await confirm(content)
    if (!result) return

    setLoading(true)
    sendEmailForLoanServiceExtension(item.id)
      .then(({ success }) => {
        if (!success) return
        toast('Email is sent successfully', { type: 'info' })
      })
      .finally(() => setLoading(false))
  }

  const getData = useMemo(() => {
    let rlt: any = []
    data.map((item, index) => {
      item.signer = `<div>${item.feasibilitySignerName || ''}</div><div>${emailObj2Txt(
        item.feasibilityEmailToLink,
        email,
      )}</div>`
      item.createdBy = `<div>${formatTime(item.createdAt)}</div><div>${item.by}</div>`
      const signature = item.signature
      if (!signature) item.signatureStatus = ''
      else {
        const signers = signature.signers || []
        const signedCount = signers.filter((s: any) => s.signed).length
        const total = signers.length
        // The address each signing link was sent to, so a request already out can be checked
        // against who was meant to receive it. Escaped, because this cell is rendered as raw HTML.
        const sentTo = signers
          .map(
            (s: any) =>
              `<div title="${escapeHtml(s.name)}">${escapeHtml(s.email)}${s.signed ? ' - signed' : ''}</div>`,
          )
          .join('')
        item.signatureStatus = `<div>${escapeHtml(signature.status)}</div><div class="text-[11px] text-gray-500">${signedCount} of ${total} signed</div><div class="text-[11px] text-gray-500">${sentTo}</div>`
      }
      // Neither an imported extension nor a free one has a fee request to open or re-send.
      const isPlain = item.status === IMPORTED_EXTENSION_STATUS || item.status === NO_FEE_EXTENSION_STATUS
      item.divStatus = isShared || isPlain ? (
        <div
          className={`p-1 flex gap-2 items-center rounded justify-center italic font-semibold ${getStatusBadgeClass(
            item.status,
          )}`}
        >
          {item.status}
        </div>
      ) : (
        <div
          className={`p-1 hover:underline cursor-pointer flex gap-2 items-center rounded justify-center italic font-semibold ${getStatusBadgeClass(
            item.status,
          )}
      `}
          onClick={() => onChoose(item, index)}
        >
          {item.status} {item.status !== 'Sent' && <EyeIcon className="w-4 h-4"></EyeIcon>}
          {item.status === 'Sent' && <EnvelopeIcon className="w-4 h-4" />}
        </div>
      )
      rlt.push(item)
    })
    return rlt
  }, [data, isShared, email])

  const inputLogic = (_data: any, _currentId?: number, currentKey?: string, saved?: Record<string, any>) => {
    const data = extensionInputLogic(_data)
    // A tenant that grants free extensions saves one with the fee blank. With no fee there is no
    // payment request either, so the link recipient stops being required with it.
    if (data.paymentAmount) data.paymentAmount.required = requireFeeRef.current
    if (data.feasibilityEmailToLink) data.feasibilityEmailToLink.required = requireFeeRef.current
    if (data.documentTemplate) {
      // Copy the allowlist so a saved template that is no longer checked can still show on this
      // row without leaking into the shared options a new extension would see.
      const options = { ...templateOptionsRef.current }
      const current = `${data.documentTemplate.value || ''}`.trim()
      if (current && !options[current]) options[current] = current
      data.documentTemplate.options = options
    }
    // Only seed on open (no currentKey), so clearing a field by hand is not undone as you type.
    if (!currentKey) {
      Object.keys(defaultsRef.current).forEach((key) => {
        if (!data[key] || data[key].value) return
        // An extension that already holds this key has an answer for it, and an empty one is the
        // servicer having cleared the field. Seeding the default back over it is why a duplicated
        // Borrower 2 could not be deleted: it reappeared every time the row was reopened.
        if (saved && key in saved) return
        // Through InputConvert like a saved value is: the defaults carry dates as MM/DD/YYYY, which
        // the date input rejects as "Invalid Date Format." and blocks the save until each is retyped.
        data[key].value = InputConvert(data[key], defaultsRef.current[key])
      })
    }
    return data
  }

  const onGenerateDocument = async (item: any) => {
    if (!item.documentTemplate) {
      toast('Pick an Extension Document on this extension first.', { type: 'error' })
      return
    }
    setLoading(true)
    try {
      await downloadExtensionDocument(item.id, item.documentTemplate)
    } finally {
      setLoading(false)
    }
  }

  const onSendDocument = async (item: any) => {
    if (!item.documentTemplate) {
      toast('Pick an Extension Document on this extension first.', { type: 'error' })
      return
    }
    const recipients = emailObj2Txt(item.feasibilityEmailToLink, email)
    const content = (
      <div className="text-gray-400 mb-4 text-[18px]">
        Send the extension document to the borrower?
        <br />
        <span className="text-gray-600">{item.documentTemplate}</span>
        <br />
        <span className="text-gray-600">{recipients}</span>
      </div>
    )
    const result = await confirm(content)
    if (!result) return

    setLoading(true)
    try {
      const res = await sendExtensionDocument(item.id, item.documentTemplate, item.feasibilityEmailToLink)
      if (res.success) toast('Extension document sent', { type: 'info' })
      else toast(res.status || 'Failed to send the extension document', { type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const onSendForSignature = async (item: any) => {
    if (!item.documentTemplate) {
      toast('Pick an Extension Document on this extension first.', { type: 'error' })
      return
    }
    // Who actually gets a link is resolved on the server from the agreement's signer names and the
    // borrower records, so it is asked for rather than guessed here: the dialog has to show the
    // same list the send will use, and a signer silently dropped for want of an email is exactly
    // what the servicer needs to catch before anything goes out.
    setLoading(true)
    let preview: Record<string, any> | null = null
    try {
      preview = await getExtensionSignatureRecipients(item.id)
    } catch {
      return
    } finally {
      setLoading(false)
    }

    const recipients: Array<{ name: string; email: string }> = preview?.recipients || []
    const issues: string[] = preview?.issues || []
    if (!recipients.length) {
      toast(issues[0] || 'This extension has no borrower signer with an email address.', { type: 'error' })
      return
    }

    const content = (
      <div className="text-gray-400 mb-4 text-[18px]">
        Send the extension agreement out for signature?
        <br />
        <span className="text-gray-600">{item.documentTemplate}</span>
        <div className="mt-3 text-left text-[14px] text-gray-600">
          <div className="font-semibold">A signing link goes to:</div>
          {recipients.map((recipient, index) => (
            <div key={`${recipient.email}-${index}`}>
              {recipient.name} - {recipient.email}
            </div>
          ))}
        </div>
        {issues.length > 0 && (
          <div className="mt-3 text-left text-[14px] text-red-600">
            {issues.map((issue) => (
              <div key={issue}>{issue}</div>
            ))}
          </div>
        )}
        <div className="mt-3 text-gray-600 text-[14px]">
          Each borrower signer receives their own signing link. The executed copy is filed on the loan and
          shared to the borrower portal.
        </div>
      </div>
    )
    const result = await confirm(content)
    if (!result) return

    setLoading(true)
    try {
      const res = await sendExtensionForSignature(item.id, item.documentTemplate)
      if (res.success) {
        toast('Extension agreement sent for signature', { type: 'info' })
        await init()
      } else toast(res.status || 'Failed to send for signature', { type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const onOpenEmail = (item: Record<string, any>) => {
    setAttachAgreement(false)
    setEmailRow(item)
  }

  const onSubmitEmail = async (emailData: Record<string, any>, emails: Record<string, string>) => {
    if (!emailRow) return
    const res = await emailExtension(emailRow.id, {
      emails,
      subject: emailData.subject,
      content: emailData.comment,
      files: emailData.files || [],
      attachAgreement: attachAgreement && !!emailRow.documentTemplate,
      name: emailRow.documentTemplate,
    })
    if (res.success) toast('Extension email sent', { type: 'info' })
    else {
      toast(res.status || 'Failed to send the extension email', { type: 'error' })
      // Keeps the dialog open, so the servicer does not lose what they wrote.
      throw new Error(res.status)
    }
  }

  const renderExtensionActions = (row: Record<string, any>) => {
    if (isShared) return <></>
    const enabled = !!row.documentTemplate
    const className = `p-1 rounded ${enabled ? 'cursor-pointer hover-shadow1 text-shade-blue' : 'opacity-40'}`
    return (
      <>
        <span className="p-1 rounded cursor-pointer hover-shadow1 text-shade-blue" title="Email" onClick={() => onOpenEmail(row)}>
          <EnvelopeIcon className="w-4 h-4" />
        </span>
        <span className={className} title="Download extension document" onClick={() => onGenerateDocument(row)}>
          <DocumentArrowDownIcon className="w-4 h-4" />
        </span>
        {allowEmailWithoutSignature && (
          <span className={className} title="Email extension document to borrower" onClick={() => onSendDocument(row)}>
            <PaperAirplaneIcon className="w-4 h-4" />
          </span>
        )}
        <span className={className} title="Send out for signature" onClick={() => onSendForSignature(row)}>
          <PencilSquareIcon className="w-4 h-4" />
        </span>
      </>
    )
  }

  // The grid confirms first, and Api toasts the backend's "Successfully Deleted!",
  // so this only has to run the delete and refresh the list.
  const onRemoveExtension = async (id: number) => {
    setLoading(true)
    try {
      const res = await deleteLoanServiceExtension(id)
      if (res.success) await init()
    } finally {
      setLoading(false)
    }
  }

  const onChangeNote = (key: string, value: string) => {
    noteValueRef.current = value
    const newInputs = cloneDeep(noteInputs)
    newInputs[key].value = value
    newInputs[key].error = InputValidate(newInputs[key])
    setNoteInputs(newInputs)
  }

  const onBlurNote = async (key: string) => {
    if (isShared || key !== 'notes') return
    const value = noteValueRef.current
    setLoading(true)
    await postServicingNotes({
      key: 'globalNotes',
      value,
    })
    setLoading(false)
  }

  const saveLinks = async (currentLinks: IEmailButton[]) => {
    setLoading(true)
    await postServicingNotes({ key: 'extensionLinks', value: currentLinks })
    setLoading(false)
  }

  const onBlurLink = async () => {
    await saveLinks(linksRef.current)
  }

  const handleExtensionLinksChange = async (next: IEmailButton[]) => {
    const prevLen = linksRef.current.length
    linksRef.current = next
    setLinks(next)
    if (prevLen !== next.length) {
      await saveLinks(next)
    }
  }

  const onSubmit = (currentId: any, values: Record<string, any>) => {
    return new Promise(async (resolve) => {
      if (isShared) {
        resolve(values)
        return
      }
      setLoading(true)
      if (!currentId) {
        const res = await createLoanServiceExtension(values)
        if (res.success) {
          values.id = res.data.id
          values.status = res.data.status
          values.signer = `<div>${values.feasibilitySignerName}</div><div>${values.feasibilityEmailToLink}</div>`
          values.createdBy = `<div>${formatTime(res.data.createdAt)}</div><div>${res.data.by}</div>`
          init()
        }
      } else {
        const res = await updateLoanServiceExtension(values, currentId)
        if (res.success) {
          init()
        }
      }
      resolve(values)
    })
  }

  return (
    <div className="loan-extension-container">
      <h2 className="text-2xl flex items-center gap-1 font-bold mb-2">
        <span>Loan Extension</span>
      </h2>
      <LayoutLoading show={loading} />
      <div className="mb-4">
        <FormTable
          cols={4}
          header={header}
          inputs={inputs}
          inputLogic={inputLogic}
          data={getData}
          onSubmit={isShared ? null : onSubmit}
          onRemove={isShared ? null : (id: any) => onRemoveExtension(id)}
          permission={isShared ? 3 : 1}
          renderAdditionalActions={renderExtensionActions}
          addText={isShared ? '' : 'Click Here to add New Extension'}
        />
      </div>
      <RenderInput
        input={{ ...noteInputs.notes, readOnly: isShared }}
        Key="notes"
        onChange={onChangeNote}
        onBlur={onBlurNote}
      />
      {!isShared && (
        <div className="mt-4 grid grid-cols-1 gap-4">
          <EmailLinksEditor
            value={links}
            onChange={handleExtensionLinksChange}
            onBlur={onBlurLink}
            properties={{ nonRemovalLink: 'DEFAULT_LINK' }}
          />
        </div>
      )}
      {!isShared && emailRow && (
        <SendEmailDialog
          title={`Email Extension ${emailRow.id}`}
          loadParties
          defaultValues={borrowerDefaults}
          emailData={{
            subject: `Loan Extension${emailRow.extensionNumber ? ` #${emailRow.extensionNumber}` : ''} - Loan ${Api.getLoanNumber()}`,
            comment: '',
            files: emailRow.documents || [],
          }}
          hadFile
          filePath={`loan/${Api.getLoanNumber()}/extension`}
          modalId="sendEmailLoanExtension"
          onSubmit={onSubmitEmail}
          onClose={() => setEmailRow(null)}
        >
          {emailRow.documentTemplate && allowEmailWithoutSignature && (
            <label className="flex items-center gap-2 my-3 text-[14px] text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                className="rounded border-gray-300"
                checked={attachAgreement}
                onChange={(e) => setAttachAgreement(e.target.checked)}
              />
              Attach the extension agreement ({emailRow.documentTemplate})
            </label>
          )}
        </SendEmailDialog>
      )}
      {!isShared && stripeIntent && <StripeIntentDialog data={stripeIntent} onClose={() => setStripeIntent(null)} />}
    </div>
  )
}
